/**
 * Attorney coverage — the state -> attorney routing table (slice 1, DARK).
 *
 * Nothing routes or sends off this table yet; it is maintained ahead of the routing engine
 * so coverage is correct before the first question is ever dispatched.
 *
 * Authorization is server-side. `attorney_coverage` itself is deny-by-default (RLS on, no
 * policies, and no grant to `anon`/`authenticated`), so the browser can only reach it through
 * these SECURITY DEFINER RPCs, which gate on an ACTIVE profile: read is admin/superadmin/
 * manager, write is admin/superadmin only. The route guard mirrors that; the functions
 * enforce it.
 *
 * See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 */

import { supabase } from "./supabase";

export interface AttorneyCoverageRow {
  id: string;
  state_code: string;
  attorney_id: string;
  attorney_name: string | null;
  attorney_email: string | null;
  is_backup: boolean;
  active: boolean;
  /** Server verdict from attorney_usable_coverage() — the ONE definition of covered. The UI
   *  must not recompute this: four divergent copies of the rule was audit H4. */
  usable: boolean;
  /** Why not, when usable is false: backup_not_primary | inactive | not_yet_effective |
   *  expired | assignee_deactivated | assignee_no_longer_attorney. */
  unusable_reason: string | null;
  effective_from: string | null;
  effective_to: string | null;
  notes: string | null;
  updated_at: string;
}

export interface AttorneyCandidate {
  id: string;
  full_name: string | null;
  email: string | null;
}

/** Postgres error text -> something an operator can act on. */
function friendlyError(message: string): string {
  if (message.includes("not_authorized")) {
    return "You do not have permission to change attorney coverage (admin or superadmin only).";
  }
  if (message.includes("invalid_state_code")) {
    return "Enter a two-letter state code, for example TX.";
  }
  if (message.includes("attorney_not_active")) {
    return "That person is not an active attorney account. Give them the Attorney role first.";
  }
  if (message.includes("coverage_not_found")) {
    return "That coverage row no longer exists — refresh and try again.";
  }
  // The partial unique index is the one constraint an operator can hit by ordinary use.
  if (message.includes("attorney_coverage_one_active_primary_per_state")) {
    return "That state already has an active primary attorney. Deactivate the existing one first, or add this person as a backup.";
  }
  if (message.includes("attorney_coverage_no_duplicate_active_pairing")) {
    return "That attorney is already listed for this state in that role.";
  }
  return message;
}

export async function listAttorneyCoverage(
  includeInactive = false,
): Promise<AttorneyCoverageRow[]> {
  const { data, error } = await supabase.rpc("list_attorney_coverage", {
    p_include_inactive: includeInactive,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyCoverageRow[];
}

export async function listAttorneyCandidates(): Promise<AttorneyCandidate[]> {
  const { data, error } = await supabase.rpc("list_attorney_candidates");
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyCandidate[];
}

export interface UpsertCoverageInput {
  id?: string | null;
  state_code: string;
  attorney_id: string;
  is_backup: boolean;
  effective_from?: string | null;
  effective_to?: string | null;
  notes?: string | null;
}

export async function upsertAttorneyCoverage(
  input: UpsertCoverageInput,
): Promise<string> {
  const { data, error } = await supabase.rpc("upsert_attorney_coverage", {
    p_state_code: input.state_code,
    p_attorney_id: input.attorney_id,
    p_is_backup: input.is_backup,
    p_effective_from: input.effective_from || null,
    p_effective_to: input.effective_to || null,
    p_notes: input.notes || null,
    p_id: input.id ?? null,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return data as string;
}

/**
 * Deactivate rather than delete: a routing decision made under a past assignment has to stay
 * explainable after the assignment changes.
 */
export interface SetCoverageActiveResult {
  state_code: string;
  state_still_covered: boolean;
  /** Present only when the write left the state with no active primary. */
  warning: string | null;
}

export async function setAttorneyCoverageActive(
  id: string,
  active: boolean,
): Promise<SetCoverageActiveResult> {
  const { data, error } = await supabase.rpc("set_attorney_coverage_active", {
    p_id: id,
    p_active: active,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  // Deactivating is legitimate — you must be able to replace an attorney — so the server
  // reports rather than refuses. Silently leaving a state unroutable is how a question
  // becomes a 06:00 batch exception nobody sees.
  return data as SetCoverageActiveResult;
}

/** US states + DC, for the picker. Free-text entry is prohibited (BRD 3.1). */
export const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

/**
 * States that would lose ALL attorney coverage if this person were deactivated — nobody
 * else, primary or backup, could take over. Owner decision 2026-09-20: warn before
 * deactivating an attorney who is the sole source of answers for a state.
 *
 * Returns [] for anyone who is not an attorney, so the caller can ask unconditionally.
 */
export interface AttorneyStateAtRisk {
  state_code: string;
  tier: "primary" | "backup";
}

export async function getAttorneyStatesAtRisk(
  attorneyId: string,
): Promise<AttorneyStateAtRisk[]> {
  const { data, error } = await supabase.rpc("attorney_states_at_risk", {
    p_attorney_id: attorneyId,
  });
  if (error) {
    // A coverage lookup must never block deactivation; the warning is advisory.
    console.warn("attorney_states_at_risk failed", error.message);
    return [];
  }
  return (data ?? []) as AttorneyStateAtRisk[];
}
